import type { HomeSettings } from '@ergo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { AddressInput } from '../../components/AddressInput';
import { useResource } from '../../hooks';
import { useMapsKey } from '../../maps';

/**
 * The address the day's trajectory starts from. Geocoded server-side on save:
 * an address Google cannot place is still kept, it simply draws no departure pin.
 */
export function Maison() {
  const settings = useResource(() => api.settings.get(), []);
  const { key } = useMapsKey();
  const [address, setAddress] = useState('');
  const [home, setHome] = useState<HomeSettings | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setHome(settings.data.home);
    setAddress(settings.data.home?.address ?? '');
  }, [settings.data]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.settings.update({ homeAddress: address.trim() || null });
      setHome(updated.home);
      setNotice(updated.home ? 'Enregistré.' : 'Adresse effacée.');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Maison</h2>

      {settings.error && (
        <p className="error" role="alert">
          {settings.error}
        </p>
      )}

      <form className="form" onSubmit={onSubmit}>
        <label className="field">
          <span className="field__label">Adresse de départ</span>
          <AddressInput
            placeholder="12 rue des Lilas, 31000 Toulouse"
            value={address}
            onChange={setAddress}
          />
          <span className="hint">
            Le trajet de la journée part d’ici. Laisser vide pour l’enlever.
          </span>
        </label>

        {/* Only while the field still shows what was saved: a half-typed address has no match yet. */}
        {home && home.address === address.trim() && (
          <p className="hint">
            {home.formattedAddress ??
              (key
                ? 'Adresse introuvable : elle est enregistrée, mais le trajet ne partira pas de la maison.'
                : 'Adresse enregistrée. Sans clé Google Maps, elle ne peut pas être localisée.')}
          </p>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {notice && <span className="muted">{notice}</span>}
        </div>
      </form>
    </section>
  );
}
